/*
 * Smores Reviews Page — standalone "all reviews" surface with product tabs.
 *
 * Reads each product's Judge.me review_widget_data (emitted by the section
 * as <script type="application/json" data-reviews-data>) — the same FREE
 * metafield the PDP section uses. Builds an "All" tab (every product merged)
 * plus one tab per product, each with the correct average / count / histogram.
 * Sort + load-more operate on the active tab.
 *
 * Field mapping mirrors smores-reviews.js. It is duplicated here on purpose
 * so the (deployed, working) PDP element is never touched. The data shape is
 * documented in memory (smores-reviews-section).
 *
 * FREE-PLAN NOTE: the metafield carries the first ~15 reviews per product
 * (page 1). The counts/averages/histograms are the TRUE totals; only the
 * card LIST is capped at what the metafield holds. Page 2+ can be fetched
 * later inside loadMore() without any markup change.
 */
class SmoresReviewsPage extends HTMLElement {
  connectedCallback() {
    this.perPage = parseInt(this.dataset.perPage, 10) || 8;

    this.grid = this.querySelector('[data-grid]');
    this.sortEl = this.querySelector('[data-sort]');
    this.histogramEl = this.querySelector('[data-histogram]');
    this.loadingEl = this.querySelector('[data-loading]');
    this.emptyEl = this.querySelector('[data-empty]');
    this.loadMoreEl = this.querySelector('[data-loadmore]');
    this.countEl = this.querySelector('[data-rating-count]');
    this.tabsEl = this.querySelector('[data-tabs]');
    if (!this.grid) return;

    this.shown = this.perPage;
    this.sortMode = 'recent';
    this.showTabs = this.dataset.showTabs !== 'false';

    this.tabs = this.buildTabs();
    this.renderTabs();

    if (this.sortEl) {
      this.sortEl.addEventListener('change', () => {
        this.sortMode = this.sortEl.value;
        this.shown = this.perPage;
        this.renderCards();
      });
    }
    if (this.loadMoreEl) {
      this.loadMoreEl.addEventListener('click', () => {
        this.shown += this.perPage;
        this.renderCards();
      });
    }
    if (this.tabsEl) {
      this.tabsEl.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-tab]');
        if (btn) this.setActive(btn.dataset.tab);
      });
    }

    this.setActive('all');

    if (this.loadingEl) this.loadingEl.hidden = true;
    this.grid.setAttribute('aria-busy', 'false');
  }

  /* ── Tabs ─────────────────────────────────────────────────── */

  buildTabs() {
    const blocks = [...this.querySelectorAll('[data-reviews-data]')].map((el) => {
      let data = {};
      try { data = JSON.parse(el.textContent); } catch (e) { data = {}; }
      const reviews = this.extractReviews(data);
      return {
        key: String(el.dataset.productId || el.dataset.label || ''),
        label: el.dataset.label || this.pick(data, ['product_name', 'product_title'], 'Product'),
        reviews,
        summary: this.getSummary(data, reviews),
        histogram: this.getHistogram(data, reviews),
      };
    }).filter((b) => b.key);

    const all = {
      key: 'all',
      label: this.dataset.allLabel || 'All',
      reviews: blocks.flatMap((b) => b.reviews),
      summary: this.aggregateSummary(blocks),
      histogram: this.aggregateHistogram(blocks),
    };
    return [all, ...blocks];
  }

  aggregateSummary(blocks) {
    let count = 0;
    let weighted = 0;
    blocks.forEach((b) => {
      const c = b.summary.count || 0;
      count += c;
      weighted += (b.summary.average || 0) * c;
    });
    return { average: count ? weighted / count : 0, count };
  }

  aggregateHistogram(blocks) {
    const hist = {};
    for (let s = 1; s <= 5; s++) hist[s] = { count: 0, pct: 0 };
    blocks.forEach((b) => {
      for (let s = 1; s <= 5; s++) hist[s].count += (b.histogram[s] && b.histogram[s].count) || 0;
    });
    const total = Object.values(hist).reduce((a, b) => a + b.count, 0);
    for (let s = 1; s <= 5; s++) hist[s].pct = total ? (hist[s].count / total) * 100 : 0;
    return hist;
  }

  renderTabs() {
    if (!this.tabsEl) return;
    // "no tabs" mode (e.g. collection page): hide the bar, show merged "All".
    if (!this.showTabs) { this.tabsEl.hidden = true; return; }
    this.tabsEl.hidden = false;
    this.tabsEl.textContent = '';
    this.tabs.forEach((t) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'smores-reviews__tab';
      b.dataset.tab = t.key;
      b.setAttribute('role', 'tab');
      b.setAttribute('aria-selected', t.key === 'all' ? 'true' : 'false');
      b.textContent = t.label;
      this.tabsEl.appendChild(b);
    });
  }

  setActive(key) {
    const tab = this.tabs.find((t) => String(t.key) === String(key)) || this.tabs[0];
    if (!tab) return;
    this.activeKey = tab.key;
    this.reviews = tab.reviews;
    this.summary = tab.summary;
    this.histogram = tab.histogram;
    this.shown = this.perPage;

    if (this.tabsEl) {
      this.tabsEl.querySelectorAll('[data-tab]').forEach((b) => {
        b.setAttribute('aria-selected', String(b.dataset.tab) === String(tab.key) ? 'true' : 'false');
      });
    }

    this.updateSummary();
    this.renderHistogram();
    this.renderCards();
    if (this.emptyEl) this.emptyEl.hidden = this.reviews.length > 0;
  }

  /* ── Defensive field extraction (mirrors smores-reviews.js) ── */

  pick(obj, keys, fallback) {
    for (const k of keys) {
      if (obj && obj[k] !== undefined && obj[k] !== null && obj[k] !== '') return obj[k];
    }
    return fallback;
  }

  extractReviews(data) {
    let arr = this.pick(data, ['reviews', 'reviewWidget', 'review_list', 'items'], null);
    if (!Array.isArray(arr)) {
      arr = Object.values(data || {}).find(
        (v) => Array.isArray(v) && v.length && typeof v[0] === 'object'
      ) || [];
    }
    return arr.map((r) => ({
      rating: Number(this.pick(r, ['rating', 'score', 'stars', 'star_rating'], 0)) || 0,
      title: String(this.pick(r, ['title', 'review_title', 'subject'], '')),
      body: String(this.pick(r, ['body', 'content', 'text', 'review', 'message'], '')),
      author: String(this.pick(this.pick(r, ['reviewer'], r) || r,
        ['name', 'author', 'reviewer_name', 'display_name'], 'Anonymous')),
      date: this.pick(r, ['created_at', 'date', 'date_utc', 'reviewed_at', 'updated_at'], ''),
      verified: Boolean(this.pick(r, ['verified_buyer', 'verified', 'is_verified', 'verified_purchase'], false)),
      product: this.productOf(r),
      pictures: this.pick(r, ['pictures', 'pictures_urls', 'photos', 'images'], []) || [],
      avatar: String(this.pick(r, ['avatar_image_url', 'avatar_url', 'avatar', 'photo'], '')),
      initial: String(this.pick(r, ['reviewer_initial'], '')),
    }));
  }

  productOf(r) {
    const direct = this.pick(r, ['product_title', 'product_name'], '');
    if (direct) return String(direct);
    const p = r.product !== undefined ? r.product : r.product_handle;
    if (typeof p === 'string') return p;
    if (p && typeof p === 'object') return String(this.pick(p, ['title', 'name'], ''));
    return '';
  }

  getSummary(data, reviews) {
    let average = Number(this.pick(data, ['average_rating', 'average', 'rating', 'avg'], NaN));
    if (isNaN(average)) {
      average = reviews.length
        ? reviews.reduce((s, r) => s + (Number(r.rating) || 0), 0) / reviews.length : 0;
    }
    let count = Number(this.pick(data, ['number_of_reviews', 'count', 'reviews_count', 'total'], NaN));
    if (isNaN(count)) count = reviews.length;
    return { average, count };
  }

  getHistogram(data, reviews) {
    const hist = {};
    for (let s = 1; s <= 5; s++) hist[s] = { count: 0, pct: null };
    const raw = this.pick(data, ['histogram', 'rating_breakdown', 'distribution', 'ratings_breakdown', 'breakdown'], null);
    if (Array.isArray(raw)) {
      raw.forEach((h) => {
        const s = Number(h.rating);
        if (hist[s]) hist[s] = {
          count: Number(h.frequency != null ? h.frequency : h.count) || 0,
          pct: h.percentage != null ? Number(h.percentage) : null,
        };
      });
    } else if (raw && typeof raw === 'object') {
      for (let s = 1; s <= 5; s++) {
        const v = raw[s] !== undefined ? raw[s] : raw[String(s)];
        if (v !== undefined) hist[s].count = Number(v) || 0;
      }
    } else {
      reviews.forEach((r) => { const s = Math.round(r.rating); if (hist[s]) hist[s].count++; });
    }
    const total = Object.values(hist).reduce((a, b) => a + b.count, 0);
    for (let s = 1; s <= 5; s++) if (hist[s].pct == null) hist[s].pct = total ? (hist[s].count / total) * 100 : 0;
    return hist;
  }

  /* ── Rendering (mirrors smores-reviews.js) ───────────────────── */

  updateSummary() {
    const s = this.summary;
    if (!s) return;
    const avgEl = this.querySelector('.smores-reviews__average');
    if (avgEl) avgEl.textContent = (s.average || 0).toFixed(1);
    const stars = this.querySelector('.smores-reviews__average-stars');
    if (stars) {
      const fill = stars.querySelector('.smores-stars__fill');
      if (fill) fill.style.width = Math.max(0, Math.min(100, ((s.average || 0) / 5) * 100)) + '%';
      stars.setAttribute('aria-label', (s.average || 0).toFixed(1) + ' out of 5 stars');
    }
    if (this.countEl && s.count != null) this.countEl.textContent = s.count;
  }

  renderHistogram() {
    if (!this.histogramEl) return;
    const total = Object.values(this.histogram).reduce((a, b) => a + b.count, 0);
    if (!total) { this.histogramEl.hidden = true; return; }
    this.histogramEl.hidden = false;
    this.histogramEl.removeAttribute('aria-hidden');

    this.histogramEl.querySelectorAll('.smores-reviews__hist-row').forEach((row) => {
      const star = Number(row.dataset.rating);
      const bucket = this.histogram[star] || { count: 0, pct: 0 };
      const fill = row.querySelector('[data-hist-fill]');
      const count = row.querySelector('[data-hist-count]');
      if (fill) fill.style.width = bucket.pct.toFixed(1) + '%';
      if (count) count.textContent = '(' + bucket.count + (bucket.count === 1 ? ' review)' : ' reviews)');
    });
  }

  sortedReviews() {
    const list = this.reviews.slice();
    const time = (r) => { const t = Date.parse(r.date); return isNaN(t) ? 0 : t; };
    switch (this.sortMode) {
      case 'oldest':   return list.sort((a, b) => time(a) - time(b));
      case 'highest':  return list.sort((a, b) => b.rating - a.rating || time(b) - time(a));
      case 'lowest':   return list.sort((a, b) => a.rating - b.rating || time(b) - time(a));
      case 'pictures': return list.sort((a, b) => (b.pictures.length > 0) - (a.pictures.length > 0) || time(b) - time(a));
      default:         return list.sort((a, b) => time(b) - time(a));
    }
  }

  renderCards() {
    if (!this.reviews) return;
    const all = this.sortedReviews();
    const visible = all.slice(0, this.shown);
    this.grid.textContent = '';
    visible.forEach((r) => this.grid.appendChild(this.card(r)));
    if (this.loadMoreEl) this.loadMoreEl.hidden = all.length <= this.shown;
  }

  card(r) {
    const el = document.createElement('article');
    el.className = 'smores-reviews__card';
    el.setAttribute('role', 'listitem');

    const header = document.createElement('header');
    header.className = 'smores-reviews__card-head';

    const avatar = document.createElement('span');
    avatar.className = 'smores-reviews__avatar';
    if (r.avatar) {
      const img = document.createElement('img');
      img.src = r.avatar; img.alt = ''; img.loading = 'lazy';
      avatar.appendChild(img);
    } else {
      avatar.textContent = (r.initial || r.author.trim()[0] || '?').toUpperCase();
      avatar.classList.add('smores-reviews__avatar--initial');
    }
    header.appendChild(avatar);

    const meta = document.createElement('div');
    meta.className = 'smores-reviews__card-meta';
    meta.appendChild(this.stars(r.rating));

    const line = document.createElement('p');
    line.className = 'smores-reviews__card-byline';
    const name = document.createElement('span');
    name.className = 'smores-reviews__card-author';
    name.textContent = r.author;
    line.appendChild(name);
    if (r.date) {
      const dot = document.createElement('span');
      dot.className = 'smores-reviews__card-dot';
      dot.setAttribute('aria-hidden', 'true');
      dot.textContent = '•';
      const date = document.createElement('time');
      date.className = 'smores-reviews__card-date';
      date.textContent = this.formatDate(r.date);
      line.append(dot, date);
    }
    meta.appendChild(line);
    header.appendChild(meta);

    if (r.product) {
      const pill = document.createElement('span');
      pill.className = 'smores-reviews__card-tag';
      pill.textContent = r.product;
      header.appendChild(pill);
    }
    el.appendChild(header);

    if (r.title) {
      const title = document.createElement('h3');
      title.className = 'smores-reviews__card-title';
      title.textContent = r.title;
      el.appendChild(title);
    }
    if (r.body) {
      const body = document.createElement('p');
      body.className = 'smores-reviews__card-body';
      body.textContent = '"' + r.body + '"';
      el.appendChild(body);
    }
    return el;
  }

  stars(rating) {
    const pct = Math.max(0, Math.min(100, (Number(rating) / 5) * 100));
    const wrap = document.createElement('span');
    wrap.className = 'smores-stars smores-reviews__card-stars';
    wrap.setAttribute('role', 'img');
    wrap.setAttribute('aria-label', rating + ' out of 5 stars');
    const track = document.createElement('span');
    track.className = 'smores-stars__track';
    track.setAttribute('aria-hidden', 'true');
    track.textContent = '★★★★★';
    const fill = document.createElement('span');
    fill.className = 'smores-stars__fill';
    fill.setAttribute('aria-hidden', 'true');
    fill.style.width = pct + '%';
    fill.textContent = '★★★★★';
    wrap.append(track, fill);
    return wrap;
  }

  formatDate(value) {
    const d = new Date(value);
    if (isNaN(d.getTime())) return String(value);
    const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
    return d.getDate() + ' ' + months[d.getMonth()] + ', ' + d.getFullYear();
  }
}

if (!customElements.get('smores-reviews-page')) {
  customElements.define('smores-reviews-page', SmoresReviewsPage);
}